import { useCallback, useMemo, useState } from 'react'
import { useReportCommission } from '@/app/hooks/useReportCommission'
import { TableReportCommissionColumnsDefinition } from './components/TableReportCommissionColumnsDefinition'
import {
  useReactTable,
  getPaginationRowModel,
  getCoreRowModel,
  getFilteredRowModel,
} from '@tanstack/react-table'
import { format } from 'date-fns'
import * as XLSX from 'xlsx'
import {
  ReportCommission,
  ReportCommissionParams,
} from '@/app/services/reportService/reportComissions'
import { reportService } from '@/app/services/reportService'
import { FiltersType } from '../components/FilterReports'
import { useAuth } from '@/app/hooks/useAuth'
import { TypeCommission } from '@/app/utils/statusCommission'
import { fetchAllReportData } from '@/app/utils/fetchAllReportData'
import { useToast } from '@/view/components/ui/use-toast'

// const statusMap = {
//   [StatusCommission.PAID]: 'Pago',
//   [StatusCommission.PENDING]: 'Em andamento',
//   [StatusCommission.AVAILABLE]: 'Disponível',
//   [StatusCommission.UNPAID]: 'Recusado',
// }

export const TypeCommissionMap = {
  [TypeCommission.REFFERED]: 'Indicação',
  [TypeCommission.TRANSACTIONAL]: 'Transação',
  [TypeCommission.BONUS]: 'Bônus',
  [TypeCommission.RETROATIVE]: 'Meta Atingida ',
}
const AMOUNT_REPORTS_FETCH = Number(import.meta.env.VITE_AMOUNT_REPORTS_FETCH)

export const useReportCommissionController = () => {
  const [exportProgress, setExportProgress] = useState(0)
  const [isExporting, setIsExporting] = useState(false)
  const { data, isFetching, isLoading, setParams, params } =
    useReportCommission()
  const { isLeader, isAdmin, isManager, isSupervisor } = useAuth()
  const { toast } = useToast()

  const { columns } = TableReportCommissionColumnsDefinition()

  const table = useReactTable({
    data: data?.commissions || [],
    columns,
    getPaginationRowModel: getPaginationRowModel(),
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    state: {
      pagination: {
        pageIndex: 0,
        pageSize: data?.pagination.limit || 100,
      },
    },
  })

  const fetchAllData = useCallback(async (): Promise<ReportCommission[]> => {
    if (!data) return []

    setIsExporting(true)
    setExportProgress(0)

    try {
      return await fetchAllReportData<ReportCommission>({
        total: data.pagination.total,
        batchSize: AMOUNT_REPORTS_FETCH,
        onProgress: setExportProgress,
        fetchBatch: async ({ skip, limit }) => {
          const exportParams: ReportCommissionParams = {
            ...params,
            limit,
            skip,
          }
          const batchData = await reportService.reportCommission(exportParams)
          return batchData.commissions
        },
      })
    } finally {
      setIsExporting(false)
    }
  }, [data, params])

  const handleExportToExcel = useCallback(async () => {
    try {
      const fetchedData = await fetchAllData()

      const formattedData = fetchedData.map((commission) => ({
        seller: commission.user || '',
        team: commission.team || 'Sem equipe',
        manager: commission.manager || 'Sem gerente',
        supervisor: commission.supervisor || 'Sem supervisor',
        leader: commission.leader || 'Sem Líder',
        origin: commission.transaction_contract || '',
        valueCommission: +(commission.value / 100).toFixed(2) || 0,
        type: TypeCommissionMap[commission.type] || 'Tipo desconhecido',
        date: format(commission.createdAt, 'dd/MM/yyyy, HH:mm') || '',
      }))

      if (formattedData.length > 0) {
        const workbook = XLSX.utils.book_new()
        const worksheet = XLSX.utils.json_to_sheet(formattedData)
        XLSX.utils.book_append_sheet(workbook, worksheet, 'ExportedReport')
        XLSX.utils.sheet_add_aoa(worksheet, [
          [
            'Vendedor',
            'Equipe',
            'Gerente',
            'Supervisor',
            'Líder',
            'Origem',
            'Valor da comissão',
            'Tipo',
            'Data',
          ],
        ])
        XLSX.writeFile(workbook, 'commissions-reports.xlsx')
      }
    } catch (error) {
      console.error('Error exporting commissions report:', error)
      toast({
        title: 'Não foi possível exportar o relatório',
        description:
          'Alguns dados não puderam ser carregados. Tente novamente.',
        variant: 'destructive',
      })
    } finally {
      setIsExporting(false)
    }
  }, [fetchAllData, toast])

  const getPageIndex = () => {
    if (!data) return 0
    return Math.floor(data.pagination.skip / data.pagination.limit) + 1
  }

  const getPageCount = () => {
    if (!data) return 0
    return Math.ceil(data.pagination.total / data.pagination.limit)
  }

  const handleNextPage = () => {
    if (!data) return
    const newPage = data.pagination.skip / data.pagination.limit + 1
    if (getPageCount() === getPageIndex()) return
    const skipValue = newPage * data.pagination.limit
    setParams((p) => ({ ...p, skip: skipValue }))
  }

  const handlePreviousPage = () => {
    if (!data) return
    const newPage = data.pagination.skip / data.pagination.limit - 1
    if (newPage < 0) return
    const skipValue = newPage * data.pagination.limit
    setParams((p) => ({ ...p, skip: skipValue }))
  }

  const filters: FiltersType[] = useMemo(() => {
    const result: FiltersType[] = ['Date', 'Type']

    if (isAdmin)
      result.push('Managers', 'Supervisors', 'Leaders', 'Teams', 'Sellers')
    if (isManager) result.push('Supervisors', 'Leaders', 'Teams', 'Sellers')
    if (isSupervisor) result.push('Leaders', 'Teams', 'Sellers')
    if (isLeader) result.push('Sellers')

    result.push('Value')
    return result
  }, [isAdmin, isManager, isSupervisor, isLeader])

  return {
    table,
    filters,
    handleNextPage,
    handlePreviousPage,
    getPageIndex,
    getPageCount,
    isFetching,
    isLoading,
    setParams,
    isExporting,
    exportProgress,
    handleExportToExcel,
  }
}
